import { APP_INITIALIZER, NgModule } from '@angular/core';

import { HttpClient } from '@angular/common/http';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router, RouterModule } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { CookieService } from 'ngx-cookie-service';
import {
  AmdocsAppInitResponse,
  AmdocsAppInitService, AmdocsAuthService, AmdocsCookieService, AmdocsUserService,
  AmdocsApiService, AmdocsTimeService,
  IAppGatewayConfig,
  IAppSyncConfig,
  ILoginGatewayConfig
} from 'amdocs-auth-package';

import { CustomAmdocsAuthService } from './core/custom-amdocs-auth.service';
import { AmdocsPwaService, AmdocsUiModule } from 'amdocs-core-package';
import player from 'lottie-web';
import { provideLottieOptions } from 'ngx-lottie';
import { MarkdownModule, MARKED_OPTIONS } from 'ngx-markdown';
import { firstValueFrom, of, throwError } from 'rxjs';
import { catchError, finalize, mergeMap } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CoreModule } from './core/core.module';
import { markedOptionsFactory } from './feature/ping/shared/utils/markdown-renderer';
import { ShareModule } from './share/share.module';
import { NetworkInterceptorService } from './core/api-logger/network-interceptor.service';
// Note we need a separate function for AOT compilation
export function playerFactory() {
  return player;
}

export function initializeApp(appInitService: AmdocsAppInitService, router: Router, amdocsPwaService: AmdocsPwaService, amdocsUserService: AmdocsUserService, cookieService: AmdocsCookieService,): any {
const appInit = appInitService;
 return (): Promise<any> => {
  return firstValueFrom(
    appInitService.initApp(initQuery, true, false).pipe(
      mergeMap((appInitResponse: AmdocsAppInitResponse) => {
        handleLoginRedirect(appInitResponse, router, cookieService);
        handleAppInitError(appInit, appInitResponse);
        return of(appInitResponse);
      }),
      catchError((error) => {
        appInit.appInitError = error;
        appInit.appFinishedLoaded = true;
        document.body.className = document.body.className.replace('show-splash', '');
        return throwError(() => error);
      }),
      finalize(() => {
        appInit.appFinishedLoaded = true;
        document.body.className = document.body.className.replace('show-splash', '');
      })
    )
  );
};
}
// Add these helper functions above or below your initializer
function handleLoginRedirect(
  appInitResponse: AmdocsAppInitResponse,
  router: Router,
  cookieService: AmdocsCookieService
) {
  cookieService.delete('isLogin');
  if (window.location.href.indexOf('?code=') > -1) {
    router.navigate(['/'], { replaceUrl: true });
  }
  if (appInitResponse.success && window.location.href.indexOf('_callback') > -1) {
    cookieService.set('isLogin', 'true');
    const origPath = sessionStorage.getItem('orig-path');
    if (origPath) {
      const parts = origPath.split('?');
      const params = parts[1]
        ? JSON.parse(
            '{"' +
              decodeURI(parts[1])
                .replace(/"/g, '\\"')
                .replace(/&/g, '","')
                .replace(/=/g, '":"') +
              '"}'
          )
        : {};
      router.navigate([parts[0]], { queryParams: params });
    }
  }
}

function handleAppInitError(appInit: any, appInitResponse: AmdocsAppInitResponse) {
  if (!appInit.appInitError) {
    appInit.appInitError = appInitResponse.error;
  }
}

// Installs the API call interceptor as early as possible so that every
// subsequent fetch / XMLHttpRequest (Angular HttpClient, AppSync, DirectLine)
// is recorded into sessionStorage.
export function installApiCallInterceptor(
  networkInterceptor: NetworkInterceptorService,
): () => void {
  return () => networkInterceptor.install();
}


// Only defining playerFactory once
const appSyncConfig: IAppSyncConfig = {
  url: environment.graphQlBaseURL,
  cnsUrl: environment.cnsBaseURL,
  region: 'us-east-1',
  initAppOperationName: 'InitializeApplication'
};

const appGatewayConfig: IAppGatewayConfig = {
  url: 'https://localhost:4200/appConfig',
  region: 'us-east-1',
};

const loginGatewayConfig: ILoginGatewayConfig = {
  url: environment.loginGWBaseURL,
  region: 'eu-west-1',
  appid: environment.appId,
  refreshTokenInterval: environment.refreshTokenInterval
};

const imageVersion = environment.imgVersion;

// Export this factory to be used by ngx-lottie - defined at the top of the file

@NgModule({
  declarations: [
    AppComponent,
    //CafeteriaPopupComponent,
  ], imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    ShareModule,
    CoreModule,
    RouterModule,
    AmdocsUiModule,
    MarkdownModule.forRoot({
      loader: HttpClient,
      markedOptions: {
        provide: MARKED_OPTIONS,
        useFactory: markedOptionsFactory,
      },
    }),
    ServiceWorkerModule.register('ngsw-worker.js', { enabled: true, registrationStrategy: 'registerImmediately' })
  ], providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: installApiCallInterceptor,
      deps: [NetworkInterceptorService],
      multi: true
    },
    AmdocsAppInitService,
    AmdocsPwaService,
    AmdocsUserService,
    AmdocsCookieService,
    // Override AmdocsAuthService with CustomAmdocsAuthService
    {
      provide: AmdocsAuthService,
      useClass: CustomAmdocsAuthService,
      deps: ['ILoginGatewayConfig', AmdocsApiService, AmdocsTimeService, CookieService]
    },
    provideLottieOptions({ player: playerFactory }),
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [AmdocsAppInitService, Router, AmdocsPwaService, AmdocsUserService, AmdocsCookieService],
      multi: true
    },
    {
      provide: 'IAppSyncConfig',
      useValue: appSyncConfig
    },
    {
      provide: 'IAppGatewayConfig',
      useValue: appGatewayConfig
    },
    {
      provide: 'ILoginGatewayConfig',
      useValue: loginGatewayConfig
    },
    {
      provide: 'IImageVersion',
      useValue: imageVersion
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
}

const initQuery = `
      query InitializeApplication{
  applicationInit{
    isPOCUser
    userProfile {
        email
        upn
        name
        uuid
        country
        city
        building
        officeLocation
        office
        org {
            level1 {
                code
                name
            }
            level2 {
                code
                name
            }
            level3 {
                code
                name
            }
            level4 {
                code
                name
            }
            level5 {
                code
                name
            }
        }
        accessBridge {
          id
          displayName
          dialInNumber
        }
        appDownloaded
        learningPopupFlag
        }
        userSettings {
          message
          notificationCodes
        }
        appSettings {
            defaultTimezone
            notifications {
              label
              code
            }
            defaultApplicationShortcuts {
              appType
            }
        }
        employeeSearchHistory {
            employees {
                id
                jobTitle
                displayName
            }
        }
        applicationShortcuts {
            type
            title
            url
            isFavorite
        }

  }
}
`;
